import React, { useState } from "react";
import { apiClient } from "../../lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, FileText } from "lucide-react";

const C = { ink: "#0F172A", dim: "#475569", amber: "#B45309", forest: "#2B4C3B" };

// [key, label, kind?] — kind: "doc" | "list" | "secret" | "bool"
const TP_SECTIONS = [
  ["Company & contact", [["contact_name", "Primary contact"], ["dba", "DBA / trade name"], ["ein", "EIN"], ["years_in_business", "Years in business"], ["physical_address", "Physical address"], ["mailing_address", "Mailing address"], ["service_area", "Service area"], ["dot_number", "USDOT #"], ["mc_number", "MC #"], ["business_license_number", "Business license #"], ["business_license_expiry", "Business license expiry"]]],
  ["Fleet", [["fleet_size", "Fleet size"], ["vehicle_types", "Vehicle types", "list"], ["wheelchair_lift_count", "# wheelchair-lift vehicles"], ["stretcher_count", "# stretcher vehicles"]]],
  ["Service area", [["service_area_type", "Type"], ["service_radius_miles", "Radius (miles)"], ["service_home_address", "Radius center"], ["service_zip_codes", "ZIP codes", "list"]]],
  ["Insurance", [["auto_insurance_carrier", "Auto carrier"], ["auto_insurance_policy_no", "Auto policy #"], ["auto_insurance_effective", "Auto effective"], ["auto_insurance_expiry", "Auto expiry"], ["auto_coverage_csl", "Auto combined single limit ($)"], ["gl_insurance_carrier", "General liability carrier"], ["gl_insurance_policy_no", "GL policy #"], ["gl_insurance_expiry", "GL expiry"], ["gl_coverage_amount", "GL coverage ($)"], ["workers_comp_carrier", "Workers comp carrier"], ["workers_comp_policy_no", "Workers comp policy #"], ["workers_comp_expiry", "Workers comp expiry"]]],
  ["Documents", [["coi_doc_id", "Certificate of Insurance (COI)", "doc"], ["w9_doc_id", "W-9", "doc"], ["business_license_doc_id", "Business license", "doc"]]],
  ["Owner", [["owner_name", "Owner name"], ["owner_dob", "Owner date of birth", "secret"], ["owner_ssn_last4", "Owner SSN (last 4)", "secret"], ["owner_phone", "Owner phone"], ["owner_email", "Owner email"]]],
  ["Banking", [["bank_name", "Bank name"], ["bank_routing", "Routing #", "secret"], ["bank_account_last4", "Account # (last 4)", "secret"], ["bank_account_type", "Account type"]]],
  ["Notes", [["notes", "Additional notes"]]],
];

const DRIVER_SECTIONS = [
  ["Identity", [["address", "Home address"], ["dob", "Date of birth", "secret"], ["ssn_last4", "SSN (last 4)", "secret"], ["ein", "EIN"]]],
  ["Driver's license", [["dl_number", "License #"], ["dl_state", "State"], ["dl_expiry", "Expiry"], ["cdl", "Holds CDL", "bool"], ["chauffeur_endorsement", "Chauffeur endorsement", "bool"]]],
  ["Medical & background", [["medical_cert_expiry", "Medical cert expiry"], ["drug_screen_date", "Last drug screen"], ["background_check_date", "Background check date"], ["mvr_pull_date", "MVR pull date"], ["mvr_consent_date", "MVR consent signed"]]],
  ["Emergency contact", [["emergency_contact_name", "Name"], ["emergency_contact_phone", "Phone"], ["emergency_contact_relationship", "Relationship"]]],
  ["Documents", [["photo_doc_id", "Headshot", "doc"], ["dl_front_doc_id", "License front", "doc"], ["dl_back_doc_id", "License back", "doc"], ["medical_cert_doc_id", "Medical cert", "doc"], ["drug_screen_doc_id", "Drug screen result", "doc"], ["background_check_doc_id", "Background check report", "doc"]]],
  ["Service area", [["service_area_type", "Type"], ["service_radius_miles", "Radius (miles)"], ["service_home_address", "Radius center"], ["service_zip_codes", "ZIP codes", "list"]]],
  ["Vehicle", [["vehicle_type", "Vehicle type"], ["vehicle_make", "Make"], ["vehicle_model", "Model"], ["vehicle_year", "Year"], ["vehicle_vin", "VIN"], ["assigned_vehicle_plate", "License plate"]]],
  ["Notes", [["notes", "Additional notes"]]],
];

const isBlank = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

async function openDoc(docId) {
  try {
    const { data, headers } = await apiClient.get(`/tpt/coi/${docId}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));
    window.open(url, "_blank", "noopener");
  } catch {
    toast.error("Could not open this document");
  }
}

function Value({ k, v, kind, revealed }) {
  if (isBlank(v)) return <span className="italic" style={{ color: C.amber }}>Not provided — add after approval</span>;
  if (kind === "doc") {
    return (
      <button type="button" onClick={() => openDoc(v)} data-testid={`app-doc-view-${k}`}
        className="inline-flex items-center gap-1 text-sm font-semibold underline" style={{ color: C.forest }}>
        <FileText size={13} /> View
      </button>
    );
  }
  if (kind === "secret" && !revealed) return <span data-testid={`app-secret-${k}`}>••••</span>;
  if (kind === "bool") return <span>{v ? "Yes" : "No"}</span>;
  if (Array.isArray(v)) return <span>{v.join(", ")}</span>;
  if (typeof v === "object") return <span>{Object.values(v).filter(Boolean).join(", ")}</span>;
  return <span>{String(v)}</span>;
}

export default function ApplicationFields({ appType, fields }) {
  const [revealed, setRevealed] = useState(false);
  const sections = appType === "tp" ? TP_SECTIONS : DRIVER_SECTIONS;
  return (
    <div data-testid="application-review-fields">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: C.dim }}>Full application</div>
        <button type="button" onClick={() => setRevealed((r) => !r)} data-testid="app-reveal-sensitive-btn"
          className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: C.forest }}>
          {revealed ? <EyeOff size={13} /> : <Eye size={13} />} {revealed ? "Hide sensitive info" : "Show sensitive info"}
        </button>
      </div>
      <div className="space-y-3">
        {sections.map(([title, items]) => (
          <div key={title} className="rounded-xl p-3" style={{ background: "#F8F7F2" }} data-testid={`app-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: C.forest }}>{title}</div>
            <div className="grid grid-cols-2 gap-2">
              {items.map(([k, label, kind]) => (
                <div key={k} className={k === "notes" ? "col-span-2" : ""}>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: C.dim }}>{label}</div>
                  <div className="text-sm font-medium" style={{ color: C.ink }}>
                    <Value k={k} v={(fields || {})[k]} kind={kind} revealed={revealed} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
